import { filterChildrenByType } from 'Shared/utils';
import type { StyledColumnProps } from '@viewup/ui';
import { Badge, Box, Chip, Column, Icon, Point, Row, Typography } from '@viewup/ui';
import type { ChildrenProps } from 'viewup-lib/common';
import { postOptionsLabel } from '../../const';
import type { PostCampaignCardStatus } from '../../model/types';
import { ProgressIcon } from '../ProgressIcon';
import { getOptionIcon, progressStyle } from './PostCampaignCard.const';

export type PostCampaignCardProps = {
  campaignUuid: string;
  name: string;
};

export const PostCampaignCard = (props: ChildrenProps & PostCampaignCardProps) => {
  const { children, name } = props;

  const rightTopBadge = filterChildrenByType(children, [PostCampaignCard.Point, Badge]);
  const progress = filterChildrenByType(children, PostCampaignCard.Progress);
  const summary = filterChildrenByType(children, PostCampaignCard.Summary);
  const bottomContent = filterChildrenByType(children, PostCampaignCard.BottomContent);
  const title = filterChildrenByType(children, PostCampaignCard.Title);

  return (
    <Column bgColor='bg.primary' border='1 solid border.tertiary' br={16} gap={16} px={16} py={24}>
      <Row alignItems='center' gap={8} justifyContent='space-between'>
        {title.length > 0 ? (
          title
        ) : (
          <Row alignItems='center' gap={8}>
            <Typography color='text.primary' fontType='body.medium'>
              {name}
            </Typography>
          </Row>
        )}

        {rightTopBadge}
      </Row>

      {summary}
      {progress}
      {bottomContent}
    </Column>
  );
};

PostCampaignCard.Title = (props: ChildrenProps) => {
  return props.children;
};

export type PostingCampaignCardPointProps = {
  point?: number;
};
PostCampaignCard.Point = (props: PostingCampaignCardPointProps) => {
  const { point = 700 } = props;
  return (
    <Row alignItems='center' gap={4}>
      <Icon color='text.point' size='XS' source={Point} />
      <Typography color='text.primary' fontType='body.medium'>
        {point.toLocaleString('ko-KR')}
      </Typography>
    </Row>
  );
};

export type PostingCampaignCardSummaryProps = {
  description: string;
  options?: { [key in string]: number | boolean };
};
PostCampaignCard.Summary = (props: PostingCampaignCardSummaryProps) => {
  return (
    <Column gap={16}>
      <Typography color='text.tertiary' fontType='body2.regular'>
        {props.description}
      </Typography>

      {props?.options && (
        <Row css={{ flexWrap: 'wrap' }} gap={8}>
          {Object.entries(props.options)?.map(([key, value]) => (
            <Chip
              key={key}
              size='XS'
              startIconSource={getOptionIcon(key)}
              styledComponents={{
                root: {
                  bgColor: 'bg.infoSubtle',
                  color: 'text.primary',
                  gap: 4,
                },
              }}
              text={
                typeof value === 'number'
                  ? `${value.toLocaleString('ko-KR')}${postOptionsLabel[key]}`
                  : postOptionsLabel[key]
              }
            />
          ))}
        </Row>
      )}
    </Column>
  );
};

export type PostingCampaignCardProgressProps = {
  status: PostCampaignCardStatus;
};
PostCampaignCard.Progress = (props: PostingCampaignCardProgressProps) => {
  const style = progressStyle[props.status];
  return (
    <Row alignItems='end' gap={8}>
      <ProgressIcon {...style.apply} label='신청' type='apply' />
      <Box bgColor='border.tertiary' css={{ alignSelf: 'center', flex: 1 }} height={1} mb={8} />
      <ProgressIcon {...style.edit} label='작성' type='edit' />
      <Box bgColor='border.tertiary' css={{ alignSelf: 'center', flex: 1 }} height={1} mb={8} />
      <ProgressIcon {...style.submit} label='제출' type='submit' />
    </Row>
  );
};

PostCampaignCard.BottomContent = (props: ChildrenProps & StyledColumnProps) => {
  const { children, ...styledProps } = props;
  return <Column {...styledProps}>{children}</Column>;
};
